import { Fragment, ReactNode, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { DesignAvatar } from '@components/design-avatar';
import { IconApi } from '@components/icons/api';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconBranch } from '@components/icons/branch';
import { IconBuilder } from '@components/icons/builder';
import { IconCondition } from '@components/icons/condition';
import { IconDate } from '@components/icons/date';
import { IconDropside } from '@components/icons/dropside';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconFormatter } from '@components/icons/formatter';
import { IconHourglass } from '@components/icons/hourglass';
import { IconCode } from '@components/icons/icon-code';
import { IconWaitCondition } from '@components/icons/icon-wait-condition';
import { IconIntegration } from '@components/icons/integration';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconLoopRestart } from '@components/icons/loop-restart';
import { IconMerge } from '@components/icons/merge';
import { IconSms } from '@components/icons/phone-sms';
import { IconPlaceholder } from '@components/icons/placeholder';
import { IconReplace } from '@components/icons/replace';
import { IconTextAnonymize } from '@components/icons/text-anonymize';
import { IconTextExtract } from '@components/icons/text-extract';
import { IconWebhook } from '@components/icons/webhook';
import { Spinner } from '@components/loading-spinner/loading-spinner';
import { PremiumTag } from '@components/premium-tag';
import { Separator } from '@components/separator';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useAIAgents } from 'api/ai-agents/hooks';
import { useIntegrationList } from 'api/integrations/hooks';
import { BASE_ACTION_TYPE, JourneyActionValuesTypes } from 'api/types';
import { useFeature } from 'ui/hooks';
import { Values } from 'ui/journey-builder/node-types/types';

export type OnCreateActionSelectProps = { actionType: BASE_ACTION_TYPE; values?: Values };

type ActionItemProps<T extends BASE_ACTION_TYPE = BASE_ACTION_TYPE> = {
  dataCy?: string;
  text: string | ReactNode;
  icon: JSX.Element;
  type: T;
  values?: JourneyActionValuesTypes[T];
  onSelect?(): any;
  isPremium?: boolean;
};

type ActionGroup = {
  type: keyof (typeof locale)['en'];
  color: Color | 'aiGradient';
  actions: ActionItemProps[];
};

type IntegrationGroupType = {
  text: string;
  icon: JSX.Element;
  endpoints: ActionItemProps[];
  webhooks: ActionItemProps[];
};

type IntegrationWebhookGroupType = {
  text: string;
  icon: JSX.Element;
  webhooks: ActionItemProps[];
};

type ActionTypeSelectorProps = {
  isInsideLoop?: boolean;
  skipTypes?: BASE_ACTION_TYPE[];
  onSelect({ actionType, values }: OnCreateActionSelectProps): Promise<any>;
  isDisabled?: boolean;
};

export const ActionTypeSelector = ({
  isInsideLoop = false,
  skipTypes = [],
  onSelect,
  isDisabled,
}: ActionTypeSelectorProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: aiAgents } = useAIAgents();
  const integrations = useIntegrationList({ isTested: true });
  const { isEnabled: hasAskAIFeature } = useFeature('FEATURE_ASK_AI');
  const { isEnabled: hasAIAgentsFeature } = useFeature('FEATURE_AI_AGENTS');
  const { isEnabled: hasCodeActionFeature } = useFeature('FEATURE_CODE_ACTION');
  const { isEnabled: hasSMSFeature } = useFeature('FEATURE_SMS');
  const { isEnabled: hasCalendarActionFeature } = useFeature('FEATURE_CALENDAR_ACTION');
  const { isEnabled: hasLoopFeature } = useFeature('FEATURE_JOURNEY_LOOP');
  const skipGroups = isInsideLoop ? ['flowControl', 'wait'] : [];

  if (!hasLoopFeature) {
    skipTypes.push('journey_loop');
  }
  if (!hasAskAIFeature) {
    skipTypes.push('ask_ai');
  }
  if (!hasSMSFeature) {
    skipTypes.push('send_sms');
  }
  if (!hasAIAgentsFeature) {
    skipTypes.push('ask_ai_agent');
  }

  if (isInsideLoop) {
    skipTypes.push('calendar_event');
  }

  if (!aiAgents) {
    return <Spinner />;
  }

  const groups: ActionGroup[] = [
    {
      type: 'forms',
      color: 'elementBlue100',
      actions: [
        {
          text: t('inputForm'),
          icon: <IconForm color="elementBlue100" />,
          type: 'combo_send_form',
        },
      ],
    },
    {
      type: 'aiAgents',
      color: 'aiGradient',
      actions: aiAgents.map((aiAgent) => ({
        text: aiAgent.name,
        icon: <DesignAvatar size={24} src={aiAgent.picture} />,
        type: 'ask_ai_agent',
        values: { aiAgentId: aiAgent.shortId },
      })),
    },
    {
      type: 'communication',
      color: 'elementGreen100',
      actions: [
        {
          text: t('sendEmail'),
          dataCy: 'send-email-action',
          icon: <IconEmail color="elementGreen100" />,
          type: 'send_email',
        },
        {
          text: t('sendSms'),
          dataCy: 'send-sms-action',
          icon: <IconSms color="elementGreen100" />,
          type: 'send_sms',
        },
        {
          text: t('calendarEvent'),
          icon: <IconDate color="elementGreen100" />,
          type: 'calendar_event',
          isPremium: !hasCalendarActionFeature,
        },
      ],
    },
    {
      type: 'documents',
      color: 'elementPurpleInvert',
      actions: [
        {
          text: t('createDocument'),
          icon: <IconBuilder color="elementPurpleInvert" />,
          type: 'create_document',
        },
        {
          text: t('convertDocument'),
          icon: <IconReplace color="elementPurpleInvert" />,
          type: 'convert_document',
        },
      ],
    },
    {
      type: 'wait',
      color: 'elementOrange100',
      actions: [
        {
          text: t('waitUntilRelativeDate'),
          icon: <IconHourglass color="elementOrange100" />,
          type: 'wait_until_relative_date',
        },
        {
          text: t('waitforDays'),
          icon: <IconHourglass color="elementOrange100" />,
          type: 'wait_for_time',
        },
        {
          text: t('waitForAttributeChange'),
          icon: <IconWaitCondition color="elementOrange100" />,
          type: 'wait_for_attribute_change',
        },
        {
          text: t('merge'),
          icon: <IconMerge color="elementOrange100" />,
          type: 'journey_merge',
        },
      ],
    },
    {
      type: 'flowControl',
      color: 'teal100',
      actions: [
        {
          text: t('addToJourney'),
          icon: <IconJourneyItem color="teal100" />,
          type: 'add_to_journey',
        },
        {
          text: t('branch'),
          icon: <IconBranch color="teal100" />,
          type: 'journey_branch',
        },
        {
          text: t('condition'),
          icon: <IconCondition color="teal100" />,
          type: 'condition_v2',
        },
        {
          text: t('loop'),
          icon: <IconLoopRestart color="teal100" />,
          type: 'journey_loop',
          values: {
            days: { value: '0' },
            hours: { value: '12' },
            minutes: { value: '0' },
          },
        },
      ],
    },
    {
      type: 'special',
      color: 'elementIndigo100',
      actions: [
        {
          text: (
            <Stack space={1} orientation="horizontal">
              <Text color="elementPurpleInvert" font="regular14" text={t('askAI')} />
              <Text color="elementBlue100" font="regular14" text={t('beta')} />
            </Stack>
          ),
          icon: <IconAskAI />,
          type: 'ask_ai',
        },
        {
          text: t('placeholder'),
          icon: <IconPlaceholder color="elementIndigo100" />,
          type: 'placeholder',
        },
        {
          text: t('formatter'),
          icon: <IconFormatter color="elementIndigo100" />,
          type: 'formatter',
        },
        {
          text: t('extractText'),
          icon: <IconTextExtract color="elementIndigo100" />,
          type: 'extract_text',
        },
        {
          text: t('anonymizeText'),
          icon: <IconTextAnonymize color="elementIndigo100" />,
          type: 'anonymize_text',
        },
        {
          text: t('code'),
          icon: <IconCode color="elementIndigo100" />,
          type: 'code',
          isPremium: !hasCodeActionFeature,
        },
      ],
    },
    {
      type: 'update',
      color: 'elementPurpleInvert',
      actions: [
        {
          text: t('journeyFields'),
          icon: <IconAttributeJourney color="elementPurpleInvert" />,
          type: 'update_fields',
        },
        {
          text: t('travellerFields'),
          icon: <IconAttributeTraveller color="elementPurpleInvert" />,
          type: 'update_traveller',
        },
      ],
    },
  ];

  const filteredGroups = groups
    .map((group) => {
      if (skipGroups.includes(group.type)) {
        return undefined;
      }
      group.actions = group.actions.filter((action) => !skipTypes.includes(action.type));
      if (group.actions.length) {
        return group;
      }
      return undefined;
    })
    .filter(Boolean) as ActionGroup[];

  const integrationGroups = (integrations.data ?? [])
    .map((integration) => {
      if (integration.endpoints.length === 0 && integration.webhooks.length === 0) {
        return undefined;
      }
      return {
        text: integration.name,
        icon: integration.thumbnail ? (
          <SquareImage src={integration.thumbnail} />
        ) : (
          <IconIntegration color="elementPurpleInvert" />
        ),
        endpoints: integration.endpoints.map((endpoint) => ({
          text: endpoint.name,
          icon: <IconApi color="elementPurpleInvert" />,
          type: 'integration',
          values: { integrationEndpointId: { value: endpoint.shortId } },
        })) as ActionItemProps[],
        webhooks: integration.webhooks.map((webhook) => ({
          text: webhook.name,
          icon: <IconWebhook color="elementPurpleInvert" />,
          type: 'webhook',
          values: { integrationWebhookId: { value: webhook.shortId } },
        })) as ActionItemProps[],
      };
    })
    .filter(Boolean) as IntegrationGroupType[];

  const handleSelect = (props: OnCreateActionSelectProps) => {
    if (!isDisabled) {
      onSelect(props);
    }
  };

  return (
    <ActionGroupContainer>
      {filteredGroups.map((group, index) => (
        <Fragment key={group.type}>
          <div>
            <Text
              text={t(group.type)}
              caps
              font="bold14"
              color={group.color === 'aiGradient' ? undefined : group.color}
              aiGradient={group.color === 'aiGradient'}
            />
            <Stack>
              {group.actions.map((action, index) => (
                <ActionItem
                  {...action}
                  key={`${group.type}-${index}`}
                  onSelect={() =>
                    action.isPremium
                      ? null
                      : handleSelect({ actionType: action.type, values: action.values })
                  }
                  isPremium={action.isPremium}
                />
              ))}
            </Stack>
          </div>
          {index !== filteredGroups.length - 1 || integrationGroups.length > 0 ? (
            <Separator />
          ) : null}
        </Fragment>
      ))}
      {integrationGroups.length > 0 && (
        <div>
          <Text text={t('integrations')} caps font="bold14" color="textPrimary" />
          <Stack>
            {integrationGroups.map((integration, index) => (
              <IntegrationGroup integration={integration} key={`${integration.text}-${index}`}>
                <IntegrationGroupContainer>
                  {integration.endpoints.map((endpoint, index) => (
                    <ActionItem
                      {...endpoint}
                      key={`${endpoint.text}-${index}`}
                      onSelect={() =>
                        handleSelect({ actionType: 'integration', values: endpoint.values })
                      }
                    />
                  ))}
                  {integration.webhooks.map((webhook, index) => (
                    <ActionItem
                      {...webhook}
                      key={`${webhook.text}-${index}`}
                      onSelect={() =>
                        handleSelect({ actionType: 'webhook', values: webhook.values })
                      }
                    />
                  ))}
                </IntegrationGroupContainer>
              </IntegrationGroup>
            ))}
          </Stack>
        </div>
      )}
    </ActionGroupContainer>
  );
};

const ActionGroupContainer = styled(Stack).attrs({
  space: 4,
})({
  marginLeft: pxToRem(8),
});

const IntegrationGroupContainer = styled(Stack)({
  marginLeft: pxToRem(16),
});

const IntegrationGroup = ({
  integration,
  children,
}: {
  integration: IntegrationGroupType | IntegrationWebhookGroupType;
  children: ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  return (
    <>
      <ActionItemContainer onClick={() => setIsCollapsed(!isCollapsed)}>
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" align="center" space={4}>
            {integration.icon}
            <Text text={integration.text} font="regular14" />
          </Stack>
          <Rotate isRotated={!isCollapsed}>
            <IconDropside color="buttonIconPositive" />
          </Rotate>
        </Stack>
      </ActionItemContainer>
      <Collapse isCollapsed={isCollapsed}>{children}</Collapse>
    </>
  );
};

const ActionItem = ({ onSelect, icon, text, dataCy, isPremium }: ActionItemProps) => (
  <ActionItemContainer onClick={onSelect} data-cy={dataCy} data-disabled={isPremium}>
    <Stack orientation="horizontal" align="center" justify="space-between">
      <ActionItemStack data-disabled={isPremium}>
        {icon}
        <Text text={text} font="regular14" />
      </ActionItemStack>
      {isPremium && <PremiumTag />}
    </Stack>
  </ActionItemContainer>
);

const ActionItemContainer = styled.button({
  borderRadius: pxToRem(4),
  paddingLeft: pxToRem(8),
  lineHeight: pxToRem(20),
  height: pxToRem(40),
  ':hover': {
    backgroundColor: theme.color.uiTrans5,
    cursor: 'pointer',
  },
  '> div': {
    width: '100%',
  },
  '&[data-disabled="true"]': {
    cursor: 'not-allowed',
  },
});

const ActionItemStack = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 3,
})({
  '&[data-disabled="true"]': {
    opacity: 0.5,
  },
});

const locale = {
  en: {
    forms: 'Forms',
    documents: 'Documents',
    createDocument: 'Create document',
    calendarEvent: 'Calendar event',
    code: 'Code',
    convertDocument: 'Convert document to PDF',
    inputForm: 'Input form',
    aiAgents: 'AI agents',
    communication: 'Communication',
    sendEmail: 'Send email',
    sendSms: 'Send SMS',
    wait: 'Wait',
    waitUntilFixedDate: 'Wait until # date',
    waitUntilRelativeDate: 'Wait until # date',
    waitforDays: 'Wait for #',
    waitForAttributeChange: 'Wait for field change',
    flowControl: 'Flow control',
    branch: 'Branch',
    loop: 'Loop',
    special: 'Special',
    placeholder: 'Placeholder',
    formatter: 'Formatter',
    askAI: 'Ask AI',
    integrations: 'Custom integrations',
    webhooks: 'Custom webhooks',
    condition: 'Condition',
    merge: 'Wait for other actions',
    beta: '(Beta)',
    addToJourney: 'Add traveller to journey',
    update: 'Update',
    journeyFields: 'Journey fields',
    travellerFields: 'Traveller fields',
    anonymizeText: 'Anonymize text',
    extractText: 'Extract text',
  },
};
